// apps/web/src/api/errors.ts
// Spec §13.3, §14.7, §20, §22.3. Three error classes surfaced by the API
// client + localized error-message mapping + sensitive-field redaction.
import { ApiErrorSchema, ErrorCode, type ValidationIssue } from '@magimp/shared';
import { i18n } from '../i18n/index.js';

export class ApiClientError extends Error {
  constructor(
    public httpStatus: number,
    public code: string,
    message: string,
    public issues?: ValidationIssue[],
    // WHY: typed discriminators (e.g. `meta.gate === 'ITEM_PHOTO_COUNT_INVALID'`)
    // travel on the shared `ApiErrorSchema.meta` envelope. Surfacing them here
    // lets call sites match by the typed field instead of free-text on
    // `message`, which is a source of locale drift and brittle UX.
    public meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export class NetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

/**
 * @aiPurpose guards against stale-token application and stale-auth-loss
 *   clearing when the session revision changes (logout/re-login) during
 *   an async `coordinatedRefresh()` call. Thrown by the coordinator
 *   itself — never by callers.
 * @aiInvariant extends NetworkError so api/client.ts preserves the
 *   current session. Callers in the realtime path treat it identically
 *   to other transient errors (no `clear()`, no reconnect).
 *   SessionChangedError is always thrown BEFORE `setAccessToken()` runs
 *   or `clear()`-side-effect decisions are made — the stale outcome is
 *   never applied.
 * @aiSecurity prevents two categories of session-revision race:
 *   1. A successful refresh whose token belongs to a prior session
 *      (no-op `setAccessToken` — the new session already has its token).
 *   2. A 4xx auth-loss error from a prior session that would otherwise
 *      `clear()` the current session.
 */
export class SessionChangedError extends NetworkError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'SessionChangedError';
  }
}

/**
 * Subclass of NetworkError signaling a 429 rate-limit response from the
 * backend's @Throttle decorator. Still a NetworkError so api/client.ts
 * preserves the session, but tagged so the React Query retry policy can
 * exclude it — retrying a throttled request immediately just amplifies
 * the back-pressure that @Throttle exists to dampen.
 */
export class ThrottleError extends NetworkError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'ThrottleError';
  }
}

/**
 * Attempt to parse the response body as the shared ApiError envelope. When the
 * envelope validates, surfaces `code`, `message`, and `issues[]` to callers.
 * Falls back to `INTERNAL_ERROR` when the body is non-JSON, the envelope shape
 * doesn't match, or the response has already been consumed.
 */
export async function parseApiError(res: Response): Promise<ApiClientError> {
  try {
    const raw = (await res.json()) as unknown;
    const parsed = ApiErrorSchema.safeParse(raw);
    if (parsed.success) {
      return new ApiClientError(
        res.status,
        parsed.data.code,
        parsed.data.message,
        parsed.data.issues,
        parsed.data.meta,
      );
    }
    // WHY: envelope didn't validate, but a `code`/`message` field may still be
    //   usable for display. Avoids dropping useful diagnostics on a partial
    //   match (e.g. legacy/proxy responses).
    const body = raw as { code?: unknown; message?: unknown };
    const code = typeof body?.code === 'string' ? body.code : ErrorCode.INTERNAL_ERROR;
    const message = typeof body?.message === 'string' ? body.message : `HTTP ${res.status}`;
    return new ApiClientError(res.status, code, message);
  } catch {
    return new ApiClientError(res.status, ErrorCode.INTERNAL_ERROR, `HTTP ${res.status}`);
  }
}

/**
 * Operation context lets callers disambiguate the 401 `AUTH_INVALID_CREDENTIALS`
 * surface: 'login' and 'change-password' intentionally use different
 * translation keys. Spec §14.7 mapping.
 */
export type ErrorOperation = 'login' | 'change-password';

const MESSAGE_KEYS = {
  [ErrorCode.AUTH_TOKEN_INVALID]: 'errors:auth.tokenInvalid',
  [ErrorCode.AUTH_REFRESH_INVALID]: 'errors:auth.refreshInvalid',
  [ErrorCode.AUTH_REFRESH_REUSED]: 'errors:auth.refreshReused',
  [ErrorCode.UNAUTHORIZED]: 'errors:auth.unauthorized',
  [ErrorCode.FORBIDDEN]: 'errors:domain.forbidden',
  [ErrorCode.USER_NOT_FOUND]: 'errors:domain.userNotFound',
  [ErrorCode.USER_EMAIL_TAKEN]: 'errors:domain.userEmailTaken',
  [ErrorCode.CANNOT_MODIFY_SELF]: 'errors:domain.cannotModifySelf',
  [ErrorCode.VALIDATION_ERROR]: 'errors:domain.validation',
  [ErrorCode.NOT_FOUND]: 'errors:domain.notFound',
  [ErrorCode.RATE_LIMIT_EXCEEDED]: 'errors:rateLimit.exceeded',
  [ErrorCode.INVALID_TRANSITION]: 'errors:domain.invalidTransition',
  [ErrorCode.GATE_FAILED]: 'errors:domain.gateFailed',
  [ErrorCode.REASON_REQUIRED]: 'errors:domain.reasonRequired',
  [ErrorCode.DOMAIN_CONFLICT]: 'errors:domain.conflict',
  [ErrorCode.NO_LOGIST_AVAILABLE]: 'errors:domain.noLogistAvailable',
  [ErrorCode.SHIFT_CANNOT_LEAVE_LAST]: 'errors:domain.shiftCannotLeaveLast',
  [ErrorCode.ACTOR_HAS_OPEN_ASSIGNMENT]: 'errors:domain.actorHasOpenAssignment',
  [ErrorCode.STORAGE_FORBIDDEN]: 'errors:domain.storageForbidden',
  [ErrorCode.ROLE_PERMISSIONS_STALE]: 'errors:domain.permissionsStale',
  // WHY (#1363): the create modals render their own inline blocking message
  //   with an open-existing action. This default mapping just gives any
  //   OTHER surface that hits a phone collision a sensible toast.
  [ErrorCode.PHONE_ALREADY_EXISTS]: 'errors:domain.phoneAlreadyExists',
  // WHY (#1398): the call-center self-service New-lead drawer renders its OWN
  //   inline localized message for an own-history phone collision and keeps the
  //   drawer open (matched on `code`, never message text). This default mapping
  //   gives any OTHER surface that hits the code a sensible fallback toast.
  [ErrorCode.CALL_CENTER_DUPLICATE_OWN_PHONE]: 'errors:domain.callCenterOwnDuplicate',
  [ErrorCode.INTERNAL_ERROR]: 'errors:generic.unavailable',
} as const satisfies Partial<Record<string, string>>;

// WHY: typed-gate map narrows `GATE_FAILED` to a per-gate locale key. The
// match is on `err.meta.gate` — never on `err.message` — so locale drift in
// the backend's free-text never shifts the toast UX.
const GATE_KEYS = {
  ITEM_PHOTO_COUNT_INVALID: 'errors:domain.requestItemMissingPhoto',
  ANSWERED_CARRIERS_BLOCK_RETURN: 'errors:domain.returnBlockedByAnsweredCarrier',
  ANSWERED_CARRIER_REQUIRED: 'errors:domain.processNoAnsweredCarrier',
  WINNER_REQUIRED: 'errors:domain.winnerRequired',
} as const satisfies Partial<Record<string, string>>;

export type ErrorMessageKey =
  | (typeof MESSAGE_KEYS)[keyof typeof MESSAGE_KEYS]
  | (typeof GATE_KEYS)[keyof typeof GATE_KEYS]
  | 'errors:auth.wrongCurrentPassword'
  | 'errors:auth.invalidCredentials';

/** Spec §14.7 error-code → message-key mapping, with operation disambiguation
 * for `AUTH_INVALID_CREDENTIALS` (login vs change-password) and typed-gate
 * narrowing for `GATE_FAILED` via `meta.gate`. */
export function errorMessageKey(err: unknown, context?: ErrorOperation): ErrorMessageKey {
  if (err instanceof NetworkError) return 'errors:generic.unavailable';
  if (err instanceof ApiClientError) {
    if (err.code === ErrorCode.AUTH_INVALID_CREDENTIALS) {
      return context === 'change-password'
        ? 'errors:auth.wrongCurrentPassword'
        : 'errors:auth.invalidCredentials';
    }
    if (err.code === ErrorCode.GATE_FAILED) {
      const gate = err.meta?.['gate'];
      if (typeof gate === 'string') {
        const key = (GATE_KEYS as Partial<Record<string, ErrorMessageKey>>)[gate];
        if (key) return key;
      }
    }
    return (
      (MESSAGE_KEYS as Partial<Record<string, ErrorMessageKey>>)[err.code] ??
      'errors:generic.unavailable'
    );
  }
  return 'errors:generic.unavailable';
}

/** Translates an API/client error into user-facing copy for the active locale. */
export function errorMessage(err: unknown, context?: ErrorOperation): string {
  return i18n.t(errorMessageKey(err, context));
}

// Spec §20. Regex match on the key names. Substitution is whole-value —
// never a partial mask — so a developer looking at a redacted log/toast
// immediately sees "that field was deliberately hidden".
//
// DANGER: presigned upload/download URLs are bearer credentials until
// `expiresAt`. Anyone holding the URL can PUT or GET the object without
// further authn. They MUST be redacted from any debug log, error toast,
// snapshot, or audit payload that flows through `redactSensitive`.
export const REDACT_KEYS = [
  /refresh[-_]?token/i,
  /^password$/,
  /^currentPassword$/,
  /^newPassword$/,
  /authorization/i,
  /^uploadUrl$/i,
  /^downloadUrl$/i,
  /presigned[-_]?url/i,
];

function isRedactedKey(key: string): boolean {
  return REDACT_KEYS.some((re) => re.test(key));
}

/**
 * Deep-redact: walks arrays + plain objects, replacing any value whose key
 * matches `REDACT_KEYS` with the string `'[REDACTED]'`. Primitives and other
 * object shapes (Date, Map, DOM, etc.) are returned as-is.
 */
export function redactSensitive<T>(x: T): T {
  if (x == null) return x;
  if (Array.isArray(x)) return x.map((v) => redactSensitive(v)) as unknown as T;
  if (typeof x === 'object') {
    const proto = Object.getPrototypeOf(x);
    if (proto !== Object.prototype && proto !== null) return x;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(x as Record<string, unknown>)) {
      out[k] = isRedactedKey(k) ? '[REDACTED]' : redactSensitive(v);
    }
    return out as unknown as T;
  }
  return x;
}
